import React, { useState } from 'react';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
// Reaction types for URLs
const URL_REACTIONS = [
  { type: 'like', emoji: '👍', label: 'Like', color: '#3b82f6' },
  { type: 'love', emoji: '❤️', label: 'Love', color: '#ef4444' },
  { type: 'sad', emoji: '😢', label: 'Sad', color: '#FCD34D' },
  { type: 'funny', emoji: '😂', label: 'Funny', color: '#f59e0b' },
  { type: 'best', emoji: '⭐', label: 'Best', color: '#10b981' },
  { type: 'dislike', emoji: '👎', label: 'Dislike', color: '#71717a' }
];

/**
 * CompactResultRow - Ultra-compact URL display with reactions and tags
 * Designed to maximize information density and minimize scrolling
 */
const CompactResultRow = ({ 
  result, 
  token, 
  onTagClick, 
  onCategoryClick,
  showToast,
  userReactions = []
}) => {
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [expanded, setExpanded] = useState(false);
  const [reactions, setReactions] = useState(result.reactions || []);
  const [reacting, setReacting] = useState(false);

  // Extract first meaningful line from snippet
  const getFirstLine = (text) => {
    if (!text) return '';
    const sentences = text.split(/[.!?]+/);
    return sentences[0]?.trim() || text.substring(0, 100);
  };

  // Extract domain for display
  const getDomain = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace('www.', '');
    } catch {
      return url?.substring(0, 30) || '';
    }
  };

  // Handle reaction
  const handleReaction = async (reactionType) => {
    if (!token || reacting) return;
    setReacting(true);
    
    try {
      const data = await post(`/search-results/${result.id}/react`, {
        body: { reaction_type: reactionType }
      });
      setReactions(data.reactions || []);
    } catch (e) {
      console.error('Reaction error:', e);
    }
    setReacting(false);
  };

  // Get reaction count
  const getReactionCount = (type) => {
    return reactions.filter(r => r.type === type).length;
  };

  // Check if user reacted
  const hasUserReacted = (type) => {
    return userReactions.includes(type) || reactions.some(r => r.type === type && r.is_mine);
  };

  // Extract tags from content
  const extractTags = () => {
    const tags = [];
    if (result.article_type) tags.push({ label: result.article_type, color: '#FDE047' });
    if (result.root_domain?.includes('.gov')) tags.push({ label: 'Government', color: '#ef4444' });
    if (result.root_domain?.includes('.edu')) tags.push({ label: 'Academic', color: '#3b82f6' });
    if (result.match_score > 80) tags.push({ label: `${Math.round(result.match_score)}% Match`, color: '#10b981' });
    return tags;
  };

  const tags = extractTags();

  return (
    <div 
      style={{
        padding: '8px 12px',
        background: expanded ? 'rgba(252, 211, 77, 0.1)' : 'rgba(255, 255, 255, 0.3)',
        borderRadius: 8,
        marginBottom: 6,
        border: '1px solid rgba(252, 211, 77, 0.15)',
        transition: 'all 0.15s'
      }}
      data-testid={`result-row-${result.id}`}
    >
      {/* Main Row - Title, Domain, Tags */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Expand Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none',
            border: 'none',
            color: '#047857',
            cursor: 'pointer',
            padding: 0,
            fontSize: '0.8rem',
            flexShrink: 0
          }}
        >
          {expanded ? '▼' : '▶'}
        </button>

        {/* Content Area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#334155',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '60%'
              }}
              title={result.title}
            >
              {result.title || 'Untitled'}
            </a>
            
            {/* Domain Badge */}
            <span style={{
              fontSize: '0.7rem',
              color: '#71717a',
              background: 'rgba(0,0,0,0.2)',
              padding: '2px 6px',
              borderRadius: 4
            }}>
              {getDomain(result.url)}
            </span>

            {/* Tags */}
            {tags.map((tag, idx) => (
              <button
                key={idx}
                onClick={() => onTagClick && onTagClick(tag.label)}
                style={{
                  fontSize: '0.65rem',
                  color: tag.color,
                  background: `${tag.color}15`,
                  padding: '2px 6px',
                  borderRadius: 10,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {tag.label}
              </button>
            ))}

            {/* Categories */}
            {result.categories?.slice(0, 2).map((cat, idx) => (
              <button
                key={idx}
                onClick={() => onCategoryClick && onCategoryClick(cat)}
                style={{
                  fontSize: '0.65rem',
                  color: '#047857',
                  background: 'rgba(244, 114, 182, 0.15)',
                  padding: '2px 6px',
                  borderRadius: 10,
                  border: 'none',
                  cursor: 'pointer'
                }}
                title={`View all results in ${cat}`}
              >
                📁 {cat}
              </button>
            ))}
          </div>

          {/* First Line Preview */}
          <div style={{
            fontSize: '0.78rem',
            color: '#a1a1aa',
            marginTop: 3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {getFirstLine(result.snippet)}
          </div>
        </div>

        {/* Compact Reactions */}
        <div style={{ 
          display: 'flex', 
          gap: 4, 
          flexShrink: 0,
          alignItems: 'center'
        }}>
          {URL_REACTIONS.map(reaction => {
            const count = getReactionCount(reaction.type);
            const userReacted = hasUserReacted(reaction.type);
            
            return (
              <button
                key={reaction.type}
                onClick={() => handleReaction(reaction.type)}
                disabled={reacting}
                title={reaction.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '3px 6px',
                  borderRadius: 12,
                  border: 'none',
                  background: userReacted ? `${reaction.color}25` : 'transparent',
                  color: userReacted ? reaction.color : '#71717a',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  transition: 'all 0.15s'
                }}
              >
                <span>{reaction.emoji}</span>
                {count > 0 && <span style={{ fontSize: '0.65rem' }}>{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ 
          marginTop: 10, 
          paddingTop: 10, 
          borderTop: '1px solid rgba(252, 211, 77, 0.2)'
        }}>
          {/* Full Snippet */}
          <p style={{ 
            fontSize: '0.85rem', 
            color: '#d1d5db', 
            lineHeight: 1.5,
            margin: '0 0 10px 0'
          }}>
            {result.snippet || result.content || 'No description available'}
          </p>

          {/* URL and Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8
          }}>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.75rem',
                color: '#10b981',
                wordBreak: 'break-all'
              }}
            >
              {result.url}
            </a>
            
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => navigator.clipboard.writeText(result.url)}
                style={{
                  fontSize: '0.75rem',
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  cursor: 'pointer'
                }}
              >
                📋 Copy URL
              </button>
              <a
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '0.75rem',
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  background: 'rgba(245, 158, 11, 0.1)',
                  color: '#3b82f6',
                  textDecoration: 'none'
                }}
              >
                🔗 Open
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * PopularTags - Clickable popular tag cloud
 */
export const PopularTags = ({ tags = [], onTagClick }) => {
  if (tags.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      padding: '10px 12px',
      background: 'rgba(255, 255, 255, 0.4)',
      borderRadius: 10,
      marginBottom: 15
    }}>
      <span style={{ 
        fontSize: '0.8rem', 
        color: '#047857', 
        fontWeight: 600,
        marginRight: 8
      }}>
        🏷️ Popular:
      </span>
      {tags.map((tag, idx) => (
        <button
          key={idx}
          onClick={() => onTagClick(tag.label)}
          style={{
            fontSize: '0.75rem',
            padding: '4px 10px',
            borderRadius: 15,
            border: 'none',
            background: `${tag.color || '#FDE047'}20`,
            color: tag.color || '#047857',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          {tag.label}
          {tag.count && (
            <span style={{ 
              fontSize: '0.65rem', 
              opacity: 0.7 
            }}>
              ({tag.count})
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

/**
 * CompactResultsList - Container for compact result rows
 */
export const CompactResultsList = ({ 
  results = [], 
  token, 
  onTagClick, 
  onCategoryClick,
  showToast,
  emptyMessage = "No results to display"
}) => {
  if (results.length === 0) {
    return (
      <div style={{
        padding: 30,
        textAlign: 'center',
        color: '#71717a',
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 12
      }}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div data-testid="compact-results-list">
      {results.map((result, idx) => (
        <CompactResultRow
          key={result.id || idx}
          result={result}
          token={token}
          onTagClick={onTagClick}
          onCategoryClick={onCategoryClick}
          showToast={showToast}
        />
      ))}
    </div>
  );
};

export default CompactResultRow;
